/**
 * Ops staff roles on the client (2026-09-25). UX only — the API enforces
 * every rule. Mirrors services/api/src/admin/staff-roles.ts.
 */
import type { StaffRole } from '@sill/types/api';
import type { View } from '../app/view-router.js';

const RANK: Readonly<Record<StaffRole, number>> = { viewer: 1, support: 2, admin: 3, owner: 4 };

export function hasRole(role: StaffRole | undefined, min: StaffRole): boolean {
  return role !== undefined && RANK[role] >= RANK[min];
}

/** Minimum role to open each ops page. */
export const VIEW_MIN_ROLE: Partial<Record<View, StaffRole>> = {
  metrics: 'viewer',
  health: 'viewer',
  jobs: 'viewer',
  testsuite: 'viewer',
  // Testing area front page (2026-10-31). Kit buttons gate on their own page's role.
  'test-kits': 'viewer',
  accounts: 'support',
  'account-detail': 'support',
  smoke: 'support',
  // ADR-0255 — ops2026 Protocol Test Agents. The VIEW floor is
  // 'support' (reads only); the API's POST route is admin+ (role
  // split 2026-10-23 security review). The "Run" button is hidden
  // for support in the view.
  'protocol-tests': 'support',
  plans: 'support',
  'plan-detail': 'support',
  // ADR-0263 S1 — add-on reads for support, create gated below.
  'addon-detail': 'support',
  purge: 'support',
  deletions: 'support',
  'plan-new': 'admin',
  // ADR-0263 S1 — add-on create.
  'addon-new': 'admin',
  leads: 'admin',
  'staff-log': 'admin',
  settings: 'admin',
  // ADR-0229 — global identity config; approvals write the edge registry.
  'agent-registry': 'admin',
  // ADR-0231 — Trust engine shadow panel (read-only; cross-merchant data).
  'trust-engine': 'admin',
  // ADR-0235 — dispute queue shows merchant notes; resolving changes labels.
  'trust-disputes': 'admin',
  // ADR-0233 — Live homepage experiment panel. Viewer can read; the API
  // enforces admin on PUT, and the view hides the write controls below.
  experiments: 'viewer',
  // ADR-0234 — agent operator enrollment review: decides whose keys Sill honours.
  'agent-operators': 'admin',
  // ADR-0239 — install fleet redeploy (writes to merchant Cloudflare accounts).
  'install-fleet': 'admin',
  // ADR-0245 §4.4.2 — verified-crawler review. Page is viewer+ (read-only
  // monitoring is fine); the server enforces admin on Fetch now and
  // owner on Approve / Reject.
  'verified-crawlers': 'viewer',
  // ADR-0251 — attack-path decisions. Admins read the panel; the two
  // override endpoints are owner-only (enforced server-side).
  'attack-path-decisions': 'admin',
  team: 'admin',
  // Founder request 2026-10-29 — ops2026 Agencies panel. Admin-floor
  // on every API route; the SPA mirrors that.
  agencies: 'admin',
};

export function canView(role: StaffRole | undefined, view: View): boolean {
  const min = VIEW_MIN_ROLE[view];
  return min === undefined || hasRole(role, min);
}

export const ROLE_LABEL: Readonly<Record<StaffRole, string>> = {
  owner: 'Owner',
  admin: 'Admin',
  support: 'Support',
  viewer: 'Viewer',
};
